import type { DemandeDTO } from '@/features/api/extranet/dto/Demande/shared'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { isAdherentOuMandatGestion } from '@/features/domaine/regles-akto/shared'
import { LevelRegleEnum, type Dispositif } from '@/features/domaine/types/opco'
import service from '@/features/services/demande/DemandeAllService'
import type {
  SearchCodeDiplome, SearchEtablissementPrestataire,
  SearchFormacode,
  SearchNsf,
  SearchRncp,
  SearchRs
} from '@/features/services/demande/DemandeAllService.types'
import router from '@/router'
import { useCancelNavigation } from '../../../composables'
import { useDialog, type IDialog } from '../../../dialog'
import type { buildDemandeStore } from './buildDemandeStore'

type BuildStoreResult = ReturnType<typeof buildDemandeStore<DemandeDTO>>

export type BuildDemandeActionsOptions = {
  dispositif: Dispositif
  store: ReturnType<BuildStoreResult>
}

export function buildDemandeActions({ dispositif, store }: BuildDemandeActionsOptions) {

  // Cancel navigation si model modifié
  useCancelNavigation({
    canLeave: () => !store.estModifié,
  })

  // Sauvegarde et transmission
  const save = async (level: LevelRegleEnum = LevelRegleEnum.Technique) => {
    if (store.meta.$estErreur === false || store.meta.$level! > level) {
      const saveOk = await store.saveData()
      if (saveOk && level === LevelRegleEnum.Transmettre) {
        const estSousMandatGestion = (store.model as any).estSousMandatGestion ?? false
        const etatAdherentOuMandatGestion = isAdherentOuMandatGestion(store.typeContextCourant, estSousMandatGestion)
        if ((await store.submitData(etatAdherentOuMandatGestion)) === false) return
        const dialog = useDialog()

        if (etatAdherentOuMandatGestion === false) {
          await dialog.showDialog({
            type: 'Ok',
            title: "Votre demande a été transmise à l'adhérent",
            isHtml: true,
            message: `
              <div style="text-align:center;">
                Merci pour votre pré-saisie.<br/>
                Un mail vient d'être adressé à « l'adhérent » pour l'informer de votre pré-saisie et
                l'inviter à vérifier, compléter et transmettre la demande à AKTO.<br/>
                A réception de la demande, le dossier sera traité dans les meilleurs délais. <br/>
                L'équipe d’AKTO
              </div>
            `,
          })
        } else {
          await dialog.showDialog({
            type: 'Ok',
            title: 'Votre demande a été transmise à AKTO',
            message: '',
          })
        }
      }
    } else {
      const dialog = useDialog()
      await dialog.showDialog({
        message: "Veuillez corriger les erreurs et renouveler l'opération.",
        type: 'Error',
        title: 'Saisie invalide',
      })
      store.dirtifyReject(level)
    }
  }

  // Confirmation ou Refus
  const confirm = async (accept: boolean) => {
    const dialog: IDialog = useDialog()
    if (accept === false) {
      const result = await dialog.showDialog({
        message: `Confirmez-vous le refus de la demande?`,
        type: 'YesNo',
        title: 'Refuser la demande',
      })
      if (result === 'No') return
    }

    await store.confirmData(accept)
    if (accept === false) {
      await dialog.showDialog({
        message: 'La demande a été refusée',
        type: 'Ok',
        title: 'Confirmation refus',
      })

      const context = TypesContextesAutorisesEnum[store.typeContextCourant]

      router.push(`/${context}/brouillons`.toLowerCase())
    }
  }

  const downloadCerfa = async () => {
    const dialog: IDialog = useDialog()
    if (store.estModifié) {
      await dialog.showDialog({
        isHtml: true,
        message:
          '<div style="text-align:center;">La demande a été mise à jour. <br/>Merci d\'enregistrer votre demande avant de procéder à la génération du CERFA</div>',
        type: 'Ok',
      })

      return
    }

    const blob = await store.downloadCerfa()

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    try {
      a.href = url
      a.target = '_blank'

      a.download = `cerfa_${store.model?.dispositif}_${store.model?.id}_${new Date().getTime()}.pdf`
      document.body.appendChild(a)
    } finally {
      a.click()
      a.remove()

      // Libère la mémoire après utilisation
      setTimeout(() => window.URL.revokeObjectURL(url), 0)
    }
  }

  /* Recherche de CFA */
  const searchPrestataire = buildSearchPrestataire(false)

  const searchPrestataireAll = buildSearchPrestataire(true)

  function buildSearchPrestataire(tous: boolean) {
    return async (searchStr: string) => {
      if (/^\d{9,14}$/.test(searchStr)) {
        const modeRechercheTous = tous && store.typeContextCourant === TypesContextesAutorisesEnum.Prestataire
        const search: SearchEtablissementPrestataire = modeRechercheTous ? {
          search: searchStr,
          dispositif,
          modeRecherche: 'RechercherTousPrestataires'
        } : {
          search: searchStr,
          dispositif,
        }

        return await service.searchEtablissementPrestataire(
          search,
          {
            sablier: false,
            dialogMode: 'silent',
          })
      }
      return []
    }
  }

  /* Recherche de RNCP */
  const searchRNCP = (search: string | SearchRncp) =>
    service.searchRncp(search, {
      sablier: false,
      dialogMode: 'silent',
    })

  /* Recherche de NSF */
  const searchNsf = (search: string | SearchNsf) =>
    service.searchNsf(search, {
      sablier: false,
      dialogMode: 'silent',
    })

  /* Recherche de Code Diplome */
  const searchCodeDiplome = (search: string | SearchCodeDiplome) =>
    service.searchCodeDiplome(search, {
      sablier: false,
      dialogMode: 'silent',
    })

  /* Recherche de formacode */
  const searchFormacode = async (search: string | SearchFormacode) =>
    service.searchFormacode(search, {
      sablier: false,
      dialogMode: 'silent',
    })

  /* Recherche Répertoire spécifique (RS) */
  const searchRs = async (search: string | SearchRs) =>
    service.searchRs(search, {
      sablier: false,
      dialogMode: 'silent',
    })

  return {
    searchPrestataire,
    searchPrestataireAll,
    searchRNCP,
    searchNsf,
    searchCodeDiplome,
    searchFormacode,
    searchRs,
    save,
    confirm,
    downloadCerfa,
    getRncp: service.getRncpSilent,
  }
}
